import { SidebarItem } from '_/components/common/layout/AppSidebar';
import { BreadcrumbItem, ActionButton } from '_/components/common/layout/MainLayout';
import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';

// Context depth levels - higher number = deeper/more specific context
export const CONTEXT_DEPTH = {
  MAIN: 0,
  ORGANIZATION: 1,
  PROJECT: 2,
  FLOW: 3,
  COLLECTION: 3,
  ALARM_GROUP: 4,
  SOURCE: 4,
  LOGIN_POOL: 4,
  DELIVERY: 5,
  SNAPSHOT: 5,
} as const;

// Sidebar Context with depth-based priority
interface SidebarContextType {
  sidebarItems: SidebarItem[];
  setSidebarItems: (items: SidebarItem[], depth: number) => void;
  registerContext: (depth: number) => void;
  unregisterContext: (depth: number) => void;
  sidebarContent?: ReactNode;
  setSidebarContent: (content: ReactNode) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarItems, setSidebarItemsState] = useState<SidebarItem[]>([]);
  const [sidebarContent, setSidebarContentState] = useState<ReactNode>(undefined);

  // Track registered context depths and their items
  const registeredDepths = useRef<Set<number>>(new Set());
  const depthItems = useRef<Map<number, SidebarItem[]>>(new Map());

  const registerContext = useCallback((depth: number) => {
    registeredDepths.current.add(depth);
  }, []);

  const unregisterContext = useCallback((depth: number) => {
    registeredDepths.current.delete(depth);
    depthItems.current.delete(depth);

    // Restore items from the next highest priority context
    const maxDepth = Math.max(...Array.from(registeredDepths.current), -1);
    if (maxDepth >= 0 && depthItems.current.has(maxDepth)) {
      setSidebarItemsState(depthItems.current.get(maxDepth) || []);
    } else {
      setSidebarItemsState([]);
    }
  }, []);

  const setSidebarItems = useCallback((items: SidebarItem[], depth: number) => {
    // Store items for this depth
    depthItems.current.set(depth, items);

    // Only update displayed items if this is the highest priority context
    const maxDepth = Math.max(...Array.from(registeredDepths.current), -1);
    if (depth >= maxDepth) {
      setSidebarItemsState(items);
    }
  }, []);

  const setSidebarContent = useCallback((content: ReactNode) => {
    setSidebarContentState(content);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        sidebarItems: sidebarItems,
        setSidebarItems: setSidebarItems,
        registerContext: registerContext,
        unregisterContext: unregisterContext,
        sidebarContent: sidebarContent,
        setSidebarContent: setSidebarContent,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

// Top Header Context
interface TopHeaderContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  actionButtons: ActionButton[];
  setActionButtons: (buttons: ActionButton[]) => void;
  onRefresh?: () => void;
  setOnRefresh: (callback?: () => void) => void;
  refreshLoading: boolean;
  setRefreshLoading: (loading: boolean) => void;
  title: string | null;
  setTitle: (title: string | null) => void;
  editableTitle: boolean;
  setEditableTitle: (editable: boolean) => void;
  onTitleChange?: (newTitle: string) => void;
  setOnTitleChange: (callback?: (newTitle: string) => void) => void;
}

const TopHeaderContext = createContext<TopHeaderContextType | undefined>(undefined);

export const TopHeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const [actionButtons, setActionButtonsState] = useState<ActionButton[]>([]);
  const [onRefresh, setOnRefreshState] = useState<(() => void) | undefined>(undefined);
  const [refreshLoading, setRefreshLoadingState] = useState(false);
  const [title, setTitleState] = useState<string | null>(null);
  const [editableTitle, setEditableTitleState] = useState(false);
  const [onTitleChange, setOnTitleChangeState] = useState<((newTitle: string) => void) | undefined>(undefined);

  const setBreadcrumbs = useCallback((breadcrumbs: BreadcrumbItem[]) => {
    setBreadcrumbsState(breadcrumbs);
  }, []);

  const setActionButtons = useCallback((buttons: ActionButton[]) => {
    setActionButtonsState(buttons);
  }, []);

  const setOnRefresh = useCallback((callback?: () => void) => {
    setOnRefreshState(() => callback);
  }, []);

  const setRefreshLoading = useCallback((loading: boolean) => {
    setRefreshLoadingState(loading);
  }, []);

  const setTitle = useCallback((title: string | null) => {
    setTitleState(title);
  }, []);

  const setEditableTitle = useCallback((editable: boolean) => {
    setEditableTitleState(editable);
  }, []);

  const setOnTitleChange = useCallback((callback?: (newTitle: string) => void) => {
    setOnTitleChangeState(() => callback);
  }, []);

  return (
    <TopHeaderContext.Provider
      value={{
        breadcrumbs: breadcrumbs,
        setBreadcrumbs: setBreadcrumbs,
        actionButtons: actionButtons,
        setActionButtons: setActionButtons,
        onRefresh: onRefresh,
        setOnRefresh: setOnRefresh,
        refreshLoading: refreshLoading,
        setRefreshLoading: setRefreshLoading,
        title: title,
        setTitle: setTitle,
        editableTitle: editableTitle,
        setEditableTitle: setEditableTitle,
        onTitleChange: onTitleChange,
        setOnTitleChange: setOnTitleChange,
      }}
    >
      {children}
    </TopHeaderContext.Provider>
  );
};

export const useTopHeader = () => {
  const context = useContext(TopHeaderContext);
  if (!context) {
    throw new Error('useTopHeader must be used within TopHeaderProvider');
  }
  return context;
};
